import { useEffect, useRef, useState } from "react";
import { shortAddress } from "../lib/chain";
import { defaultAvatar, fileToAvatarDataUrl } from "../lib/avatar";
import { isUsernameTaken, saveProfile, UsernameTakenError, type Profile } from "../lib/social";
import { startXLogin, xLoginAvailable } from "../lib/xAuth";

type Props = {
  address: string;
  existing?: Profile;
  /** true when opened automatically right after connecting */
  firstTime?: boolean;
  onClose: () => void;
  onSaved: () => void;
};

export function ProfileModal({ address, existing, firstTime, onClose, onSaved }: Props) {
  const [username, setUsername] = useState(existing?.username ?? "");
  const [avatarUrl, setAvatarUrl] = useState(existing?.avatarUrl ?? "");
  const [bio, setBio] = useState(existing?.bio ?? "");
  const [xHandle, setXHandle] = useState(existing?.xHandle ?? "");
  const [xVerified, setXVerified] = useState(existing?.xVerified ?? false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nameTaken, setNameTaken] = useState(false);
  const [checkingName, setCheckingName] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const shownAvatar = avatarUrl.trim() || defaultAvatar(address);

  // Debounced availability check so people find out before hitting save.
  useEffect(() => {
    const name = username.trim();
    if (!name || name.toLowerCase() === (existing?.username ?? "").trim().toLowerCase()) {
      setNameTaken(false);
      setCheckingName(false);
      return;
    }
    setCheckingName(true);
    const t = setTimeout(() => {
      void isUsernameTaken(name, address)
        .then(setNameTaken)
        .catch(() => setNameTaken(false))
        .finally(() => setCheckingName(false));
    }, 400);
    return () => clearTimeout(t);
  }, [username, address, existing?.username]);

  const pickFile = async (file?: File) => {
    if (!file) return;
    setError(null);
    try {
      setAvatarUrl(await fileToAvatarDataUrl(file));
    } catch (e) {
      setError(e instanceof Error ? e.message : "could not load that image");
    }
  };

  const connectX = async () => {
    setError(null);
    if (!xLoginAvailable) {
      setError("X login isn't configured yet");
      return;
    }
    try {
      // stash the in-progress form so it survives the redirect
      sessionStorage.setItem(
        "wheel-profile-draft",
        JSON.stringify({ username, avatarUrl, bio })
      );
      await startXLogin();
    } catch (e) {
      setError(e instanceof Error ? e.message : "could not start X login");
    }
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await saveProfile({
        address,
        username: username.trim(),
        avatarUrl: avatarUrl.trim(),
        bio: bio.trim(),
        xHandle: xHandle.trim().replace(/^@/, ""),
        xVerified,
        updatedAt: Date.now(),
      });
      onSaved();
    } catch (e) {
      if (e instanceof UsernameTakenError) {
        setNameTaken(true);
        setError(`"${username.trim()}" is already taken — pick another`);
      } else {
        setError("could not save your profile");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="modal-title">{firstTime ? "CREATE YOUR PROFILE" : "YOUR PROFILE"}</h3>
        {firstTime && (
          <p className="modal-sub">
            wallet connected — set up how you show up in chat. you can skip and do this later.
          </p>
        )}

        <div className="avatar-picker">
          <button
            type="button"
            className="avatar-btn"
            onClick={() => fileRef.current?.click()}
            title="Upload a profile picture"
          >
            <img src={shownAvatar} alt="" />
            <span className="avatar-overlay">UPLOAD</span>
          </button>
          <div className="avatar-meta">
            <strong>{username.trim() || shortAddress(address)}</strong>
            <p className="modal-addr">{shortAddress(address)}</p>
            {avatarUrl && (
              <button
                type="button"
                className="chat-mini-btn"
                onClick={() => setAvatarUrl("")}
              >
                RESET PIC
              </button>
            )}
          </div>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => void pickFile(e.target.files?.[0])}
        />

        <label className="modal-label">
          Username
          <input
            className={nameTaken ? "input-bad" : ""}
            value={username}
            maxLength={24}
            placeholder="pick a name"
            onChange={(e) => setUsername(e.target.value)}
          />
          <span className={`name-hint ${nameTaken ? "bad" : "ok"}`}>
            {checkingName
              ? "checking…"
              : nameTaken
                ? "already taken — pick another"
                : username.trim()
                  ? "available"
                  : "must be unique · leave blank to show your address"}
          </span>
        </label>

        <label className="modal-label">
          Bio
          <textarea
            value={bio}
            maxLength={160}
            rows={2}
            placeholder="here for the 10×"
            onChange={(e) => setBio(e.target.value)}
          />
        </label>

        <div className="modal-label">
          𝕏 account
          {xVerified ? (
            <div className="x-connected">
              <span>𝕏 @{xHandle}</span>
              <span className="x-badge">VERIFIED</span>
              <button
                type="button"
                className="chat-mini-btn"
                onClick={() => {
                  setXVerified(false);
                  setXHandle("");
                }}
              >
                DISCONNECT
              </button>
            </div>
          ) : xLoginAvailable ? (
            <button type="button" className="x-btn" onClick={() => void connectX()}>
              𝕏 CONNECT X
            </button>
          ) : (
            <button type="button" className="x-btn" disabled title="Set VITE_X_CLIENT_ID to enable">
              𝕏 CONNECT X — COMING SOON
            </button>
          )}
        </div>

        {error && <p className="chat-error">{error}</p>}

        <div className="modal-btns">
          <button type="button" className="chat-mini-btn" onClick={onClose}>
            {firstTime ? "SKIP" : "CANCEL"}
          </button>
          <button
            type="button"
            className="chat-send"
            onClick={() => void submit()}
            disabled={busy || nameTaken || checkingName}
          >
            {busy ? "SAVING…" : "SAVE"}
          </button>
        </div>
      </div>
    </div>
  );
}
